import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Tema claro / oscuro.
 *
 * El sistema de diseno define los dos, asi que el oscuro no es un anyadido:
 * es la mitad del sistema. Por defecto se sigue la preferencia del sistema
 * operativo; en cuanto el usuario elige, su eleccion manda y se recuerda.
 */

export type Tema = 'claro' | 'oscuro';
export type PreferenciaTema = Tema | 'sistema';

const STORAGE_KEY = 'alexiarest.tema';

interface ThemeValue {
  /** Lo que ha elegido el usuario, incluido "seguir al sistema" */
  preferencia: PreferenciaTema;
  /** El tema que se esta aplicando de verdad */
  tema: Tema;
  setPreferencia: (value: PreferenciaTema) => void;
  alternar: () => void;
}

const ThemeContext = createContext<ThemeValue | null>(null);

function leerPreferencia(): PreferenciaTema {
  if (typeof window === 'undefined') return 'sistema';
  const guardada = window.localStorage.getItem(STORAGE_KEY);
  return guardada === 'claro' || guardada === 'oscuro' ? guardada : 'sistema';
}

function temaDelSistema(): Tema {
  if (typeof window === 'undefined' || !window.matchMedia) return 'claro';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'oscuro' : 'claro';
}

export function ThemeProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [preferencia, setPreferenciaState] = useState<PreferenciaTema>(leerPreferencia);
  const [sistema, setSistema] = useState<Tema>(temaDelSistema);

  // Si el usuario sigue al sistema, hay que reaccionar cuando este cambia
  // (por ejemplo al anochecer en macOS).
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => setSistema(event.matches ? 'oscuro' : 'claro');
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const tema: Tema = preferencia === 'sistema' ? sistema : preferencia;

  useEffect(() => {
    document.documentElement.setAttribute('data-tema', tema);
  }, [tema]);

  const setPreferencia = useCallback((value: PreferenciaTema) => {
    setPreferenciaState(value);
    if (value === 'sistema') window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, value);
  }, []);

  const alternar = useCallback(() => {
    setPreferencia(tema === 'oscuro' ? 'claro' : 'oscuro');
  }, [tema, setPreferencia]);

  const value = useMemo<ThemeValue>(
    () => ({ preferencia, tema, setPreferencia, alternar }),
    [preferencia, tema, setPreferencia, alternar],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeValue {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme se tiene que usar dentro de <ThemeProvider>');
  return value;
}
